import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import './SpotlightTour.css';

/* Game-style coach marks: dims the screen, cuts a glowing hole around the
 * real button being explained and shows a speech card next to it.
 * steps: [{ target: 'css selector' | null, emoji, title, body }]
 * A step whose target is missing (or null) is shown as a centred card.
 */
export default function SpotlightTour({ steps, open, onClose, labels, testid = 'spotlight-tour', idPrefix = testid }) {
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const card = useRef(null);
  const step = steps[index];
  const last = index === steps.length - 1;

  useEffect(() => { if (open) setIndex(0); }, [open]);

  const measure = useCallback(() => {
    if (!open || !step) return;
    const el = step.target ? document.querySelector(step.target) : null;
    if (!el) { setRect(null); return; }
    const r = el.getBoundingClientRect();
    if (!r.width && !r.height) { setRect(null); return; }
    setRect({ x: r.left, y: r.top, w: r.width, h: r.height });
  }, [open, step]);

  useLayoutEffect(() => {
    if (!open || !step) return undefined;
    const el = step.target ? document.querySelector(step.target) : null;
    el?.scrollIntoView?.({ block: 'nearest' });
    measure();
    const id = setInterval(measure, 400); // follows layout shifts and animations
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => { clearInterval(id); window.removeEventListener('resize', measure); window.removeEventListener('scroll', measure, true); };
  }, [open, step, measure]);

  useEffect(() => { if (open) card.current?.focus(); }, [open, index]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') setIndex((i) => Math.min(steps.length - 1, i + 1));
      if (e.key === 'ArrowLeft') setIndex((i) => Math.max(0, i - 1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, steps.length]);

  if (!open || !step) return null;

  const pad = 8;
  const hole = rect && { left: rect.x - pad, top: rect.y - pad, width: rect.w + pad * 2, height: rect.h + pad * 2 };
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
  const below = hole ? hole.top + hole.height / 2 < vh / 2 : false;
  const cardStyle = hole
    ? (below ? { top: Math.min(vh - 220, hole.top + hole.height + 14) } : { bottom: Math.max(12, vh - hole.top + 14) })
    : { top: '50%', transform: 'translateY(-50%)' };

  return createPortal(
    <div className="spot-root" data-testid={testid} role="dialog" aria-modal="true" aria-labelledby={`${testid}-title`}>
      {hole ? <div className="spot-hole" style={hole} aria-hidden /> : <div className="spot-dim" aria-hidden />}
      <div ref={card} tabIndex={-1} className="spot-card" style={cardStyle}>
        <div className="spot-card__head">
          <span className="spot-card__emoji" aria-hidden>{step.emoji || '🌱'}</span>
          <div className="min-w-0 flex-1">
            <small className="spot-card__count">{index + 1} / {steps.length}</small>
            <h2 id={`${testid}-title`} className="spot-card__title">{step.title}</h2>
          </div>
          <button type="button" className="spot-card__close" data-testid={`${idPrefix}-skip`} onClick={onClose} aria-label={labels.skip}><X size={18} /></button>
        </div>
        <p className="spot-card__body">{step.body}</p>
        <div className="spot-card__bar" aria-hidden><span style={{ width: `${((index + 1) / steps.length) * 100}%` }} /></div>
        <div className="spot-card__actions">
          <button type="button" className="spot-card__ghost" onClick={index ? () => setIndex(index - 1) : onClose}>
            {index ? <><ChevronLeft size={16} /> {labels.back}</> : labels.skip}
          </button>
          <button type="button" className="spot-card__next" data-testid={last ? `${idPrefix}-finish` : `${idPrefix}-next`} onClick={last ? onClose : () => setIndex(index + 1)}>
            {last ? labels.done : <>{labels.next} <ChevronRight size={16} /></>}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
